import React from 'react'
import { statusChip, versionChip, roleChip, userStatusChip, fontFamily, fontSize, radius } from '../../styles/theme'

// ── StatusChip: template status (Active / Draft / Archived / Deleted) ─────
type TemplateStatus = 'active' | 'draft' | 'archived' | 'deleted'

interface StatusChipProps {
  status: TemplateStatus | string
  style?: React.CSSProperties
}

export function StatusChip({ status, style }: StatusChipProps) {
  const key = (status ?? '').toLowerCase() as TemplateStatus
  const t = statusChip[key] ?? statusChip.draft
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      padding: '0 10px',
      borderRadius: radius.full,
      background: t.bg,
      color: t.text,
      border: `1px solid ${t.border}`,
      fontFamily: fontFamily.sans,
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.01em',
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
    </span>
  )
}

// ── VersionChip: version tag with dot ─────────────────────────────────────
interface VersionChipProps {
  version: string | number
  active?: boolean
  style?: React.CSSProperties
}

export function VersionChip({ version, active = false, style }: VersionChipProps) {
  const t = active ? versionChip.active : versionChip.inactive
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      height: 20,
      padding: '0 8px',
      borderRadius: radius.sm,
      background: t.bg,
      color: t.text,
      border: `1px solid ${t.border}`,
      fontFamily: fontFamily.mono,
      fontSize: '11px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
      v{version}
    </span>
  )
}

// ── RoleChip: ADMIN / EDITOR / VIEWER ────────────────────────────────────
type RoleKey = 'super-admin' | 'team-admin' | 'editor' | 'viewer'

interface RoleChipProps {
  role: RoleKey | string
  style?: React.CSSProperties
}

export function RoleChip({ role, style }: RoleChipProps) {
  const key = (role ?? '').toLowerCase() as RoleKey
  const t = roleChip[key] ?? roleChip.viewer
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      padding: '0 9px',
      borderRadius: radius.sm,
      background: t.bg,
      color: t.text,
      border: `1px solid ${t.border}`,
      fontFamily: fontFamily.mono,
      fontSize: '10px',
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {key.toUpperCase()}
    </span>
  )
}

// ── UserStatusChip: Active / Invited / Suspended ──────────────────────────
type UserStatus = 'active' | 'invited' | 'suspended'

interface UserStatusChipProps {
  status: UserStatus | string
  style?: React.CSSProperties
}

export function UserStatusChip({ status, style }: UserStatusChipProps) {
  const key = (status ?? '').toLowerCase() as UserStatus
  const t = userStatusChip[key] ?? userStatusChip.suspended
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      padding: '0 10px',
      borderRadius: radius.full,
      background: t.bg,
      color: t.text,
      border: `1px solid ${'border' in t ? (t as { border: string }).border : 'transparent'}`,
      fontFamily: fontFamily.sans,
      fontSize: '11px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
    </span>
  )
}

// ── Generic Badge ─────────────────────────────────────────────────────────
interface BadgeProps {
  children: React.ReactNode
  color?: string
  bg?: string
  border?: string
  style?: React.CSSProperties
}

export function Badge({ children, color = '#6B7180', bg = '#EEF0F1', border = '#EEF0F1', style }: BadgeProps) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 20,
      padding: '0 8px',
      borderRadius: radius.full,
      background: bg,
      color,
      border: `1px solid ${border}`,
      fontFamily: fontFamily.sans,
      fontSize: '11px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {children}
    </span>
  )
}
